import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { constructTitle } from "src/pages/reports/construct_notes";
import { DomainsCircle } from "src/pages/reports/fsci/v1/graphics/domains_circle";
import { FSCIBrandedV1Report } from "src/pages/reports/fsci/v1/types";
import { isNotNil } from "src/utils/type_guards";

import { meta } from "./meta";

/*
 * Types
 */

export type RelationshipsProps = {
    readonly report: FSCIBrandedV1Report;
};

/*
 * Main
 */

export const Relationships = (props: RelationshipsProps): React.ReactElement => {
    const { report } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="relationships" inline />

                <PageTitle>Relationships</PageTitle>
                <Body>
                    Education is a relational enterprise by nature. For Christian schools in particular, relationships
                    are important because of the incarnational nature of Christian faith, expressed through community
                    and in discipleship (John 1:14, 1 Corinthians 12:12-27, Ephesians 4:16).
                </Body>
                <Body>
                    The FSCI identified trust-filled, supportive, and authentic relationships between all school
                    constituencies, as well as with the surrounding community, as key to flourishing outcomes. The
                    constructs related to Relationship were identified for all seven survey groups (students; parents;
                    alumni; teachers; leaders/administrators; support staff; and board members), below.
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>
            {pipe(
                R.keys(report.value.results.raw.relationships),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{constructTitle(m)}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={[
                                            {
                                                label: report.value.year,
                                                value: report.value.results.raw.relationships[construct] * 100,
                                            },
                                            {
                                                label: "FSCI Benchmark",
                                                value: report.value.results.benchmarks.relationships[construct] * 100,
                                            },
                                        ]}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
